import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CollapseIcon, Phone } from 'assets/icons';
import CloseIcon from 'assets/icons/close.svg';
import { Modal } from 'components';
import { ZOOM_PHONE_ORIGIN } from 'constants/common';
import { customTwMerge as twMerge, translate } from 'utils';

const ZOOM_PHONE_EMBED_BASE = `${ZOOM_PHONE_ORIGIN}/integration/phone/embeddablephone/home`;
const ZOOM_PHONE_IFRAME_ID = 'zoom-embeddable-phone-iframe';

/** Custom notes page fields shown in Zoom Phone Smart Embed. */
const ZOOM_PHONE_NOTE_PAGE_CONFIGURATION = [
  {
    fieldName: 'Disposition',
    fieldType: 'select',
    placeholder: 'Select disposition',
    selectOptions: [
      { label: 'New to Stage', value: 'New to Stage' },
      { label: 'No response', value: 'No response' },
      {
        label: 'Interested - Follow up/Next stage',
        value: 'Interested - Follow up/Next stage'
      },
      { label: 'Not interested', value: 'Not interested' }
    ]
  },
  {
    fieldName: 'Comments',
    fieldType: 'text',
    placeholder: 'Enter comments'
  }
] as const;

export type ZoomPhoneSmartEmbedEvent = {
  type: string;
  data?: unknown;
  id?: string;
};

type ZoomPhoneSmartEmbedProps = {
  open: boolean;
  phoneNumber: string;
  /** Salesforce record (or similar) tied to this dial — used to restore UI on re-dial. */
  relatedId?: string;
  onClose: () => void;
  /** Prefill only by default; set true to dial immediately. */
  autoDial?: boolean;
  onEvent?: (event: ZoomPhoneSmartEmbedEvent) => void;
};

const postToZoomPhone = (payload: {
  type: string;
  data: Record<string, unknown>;
}) => {
  const iframe = document.getElementById(
    ZOOM_PHONE_IFRAME_ID
  ) as HTMLIFrameElement | null;
  iframe?.contentWindow?.postMessage(payload, ZOOM_PHONE_ORIGIN);
};

/**
 * Zoom Phone Smart Embed popup (iframe).
 * Uses postMessage APIs (zp-make-call, zp-init-config) and can receive
 * call/SMS/log events — see Zoom Smart Embed guide.
 * Supports minimize: keeps the iframe mounted so the Zoom session survives
 * while the user navigates the app.
 */
const ZoomPhoneSmartEmbed: FC<ZoomPhoneSmartEmbedProps> = ({
  open,
  phoneNumber,
  relatedId,
  onClose,
  autoDial = false,
  onEvent
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const pendingCallRef = useRef<{ number: string; autoDial: boolean } | null>(
    null
  );
  const isReadyRef = useRef(false);
  const sessionKeyRef = useRef<string | null>(null);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  // Zoom needs the parent origin to postMessage back (required for extensions;
  // also needed when referrer/ancestor detection fails for web apps).
  const embedUrl = useMemo(() => {
    const url = new URL(ZOOM_PHONE_EMBED_BASE);
    url.searchParams.set('originDomain', window.location.origin);
    return url.toString();
  }, []);

  const makeCall = useCallback((number: string, shouldAutoDial: boolean) => {
    if (!number) return;

    if (!isReadyRef.current) {
      pendingCallRef.current = { number, autoDial: shouldAutoDial };
      return;
    }

    postToZoomPhone({
      type: 'zp-make-call',
      data: {
        number,
        autoDial: shouldAutoDial
      }
    });
  }, []);

  const handleZoomReady = useCallback(() => {
    if (isReadyRef.current) return;
    isReadyRef.current = true;

    postToZoomPhone({
      type: 'zp-init-config',
      data: {
        enableSavingLog: false,
        enableAutoLog: false,
        enableContactSearching: false,
        enableContactMatching: false,
        disableInactiveTabCallEvent: true,
        // Zoom expects a JSON string for the custom notes page schema.
        notePageConfiguration: JSON.stringify(
          ZOOM_PHONE_NOTE_PAGE_CONFIGURATION
        )
      }
    });

    const pending = pendingCallRef.current;
    if (pending?.number) {
      postToZoomPhone({
        type: 'zp-make-call',
        data: {
          number: pending.number,
          autoDial: pending.autoDial
        }
      });
      pendingCallRef.current = null;
    }
  }, []);

  // Keep pending dial in sync without tearing down the message listener.
  // Restore when phoneNumber/relatedId change while still open (re-dial
  // keeps open: true, so minimize would otherwise stick).
  useEffect(() => {
    if (!open) {
      isReadyRef.current = false;
      pendingCallRef.current = null;
      sessionKeyRef.current = null;
      setIsMinimized(false);
      return;
    }

    const nextKey = `${relatedId ?? ''}:${phoneNumber}`;
    if (sessionKeyRef.current && sessionKeyRef.current !== nextKey) {
      setIsMinimized(false);
    }
    sessionKeyRef.current = nextKey;

    pendingCallRef.current = {
      number: phoneNumber,
      autoDial
    };
  }, [open, phoneNumber, relatedId, autoDial]);

  // Stable listener while open — zp-ready is one-shot; re-subscribing on
  // phoneNumber changes can miss it forever.
  useEffect(() => {
    if (!open) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== ZOOM_PHONE_ORIGIN) return;

      const data = event.data as ZoomPhoneSmartEmbedEvent | undefined;
      if (!data?.type?.startsWith?.('zp-')) return;

      if (data.type === 'zp-ready') {
        handleZoomReady();
      }

      if (data.type === 'zp-call-ended-event') {
        setIsMinimized(false);
      }

      onEventRef.current?.(data);
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [open, handleZoomReady]);

  useEffect(() => {
    if (!open || !phoneNumber) return;
    makeCall(phoneNumber, autoDial);
  }, [open, phoneNumber, autoDial, makeCall]);

  if (!open) return null;

  return (
    <>
      {/*
        Keep Modal open for the whole session so the iframe is never unmounted.
        When minimized: no overlay, no scroll lock, no focus trap; hide chrome
        with CSS so the page is interactive again.
      */}
      <Modal
        open={open}
        onClose={onClose}
        center={!isMinimized}
        showCloseIcon={false}
        closeOnOverlayClick={false}
        closeOnEsc={!isMinimized}
        noOverlay={isMinimized}
        blockScroll={!isMinimized}
        focusTrapped={!isMinimized}
        classNames={
          isMinimized
            ? {
                root: 'pointer-events-none !z-0',
                modal:
                  'opacity-0 pointer-events-none !p-0 !m-0 !w-px !h-px !min-w-0 !shadow-none overflow-hidden',
                overlay: 'hidden'
              }
            : undefined
        }
      >
        <div
          className={twMerge(
            'flex flex-col p-4',
            isMinimized && 'invisible w-px h-px overflow-hidden !p-0'
          )}
        >
          <div className='flex items-center justify-between gap-2 mb-3'>
            <h2 className='text-base font-semibold text-DEFAULT_TEXT'>
              {translate(
                'salesforce.opportunities.side_panel.zoom_phone_title'
              )}
            </h2>
            <div className='flex items-center gap-1 shrink-0'>
              <button
                type='button'
                onClick={() => setIsMinimized(true)}
                className='p-1.5 rounded hover:bg-GRAY5 transition-colors'
                aria-label={translate(
                  'salesforce.opportunities.side_panel.zoom_phone_minimize'
                )}
                title={translate(
                  'salesforce.opportunities.side_panel.zoom_phone_minimize'
                )}
              >
                <CollapseIcon className='h-3.5 w-3.5 text-DEFAULT_TEXT' />
              </button>
              <button
                type='button'
                onClick={onClose}
                className='p-1.5 rounded hover:bg-GRAY5 transition-colors'
                aria-label={translate(
                  'salesforce.opportunities.side_panel.zoom_phone_close'
                )}
              >
                <img src={CloseIcon} className='h-3 w-3' alt='' />
              </button>
            </div>
          </div>
          <iframe
            title='Zoom Phone Smart Embed'
            id={ZOOM_PHONE_IFRAME_ID}
            src={embedUrl}
            allow={`clipboard-read; clipboard-write ${ZOOM_PHONE_ORIGIN}; microphone; camera`}
            className='border-0 rounded-lg bg-white'
            style={{ width: 420, height: 650 }}
          />
        </div>
      </Modal>

      {isMinimized && (
        <button
          type='button'
          onClick={() => setIsMinimized(false)}
          className='fixed bottom-5 right-5 z-[1000] flex items-center gap-2 px-4 py-3 rounded-full bg-PRIMARY text-white shadow-lg hover:opacity-90 transition-opacity'
          aria-label={translate(
            'salesforce.opportunities.side_panel.zoom_phone_restore'
          )}
        >
          <Phone className='h-4 w-4 brightness-0 invert' />
          <span className='text-sm font-medium'>
            {translate(
              'salesforce.opportunities.side_panel.zoom_phone_minimized_label'
            )}
          </span>
        </button>
      )}
    </>
  );
};

export default ZoomPhoneSmartEmbed;
